import { createContext, useContext, useEffect, useState } from "react";
import { api } from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null while checking
  const [checked, setChecked] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      // Hydrate Bearer token FIRST so /auth/me has auth on initial check
      const t = localStorage.getItem("tbm_token");
      if (!t) {
        // No token = not logged in. Skip /auth/me entirely to avoid noisy 401s.
        if (!cancelled) {
          setUser(false);
          setChecked(true);
        }
        return;
      }
      api.defaults.headers.common["Authorization"] = `Bearer ${t}`;
      try {
        const { data } = await api.get("/auth/me");
        if (!cancelled) setUser(data);
      } catch {
        // Stale/invalid token — clear it so subsequent requests are clean
        localStorage.removeItem("tbm_token");
        delete api.defaults.headers.common["Authorization"];
        if (!cancelled) setUser(false);
      } finally {
        if (!cancelled) setChecked(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    // Bearer-token auth (cookies no longer used — see /lib/api.js)
    if (data?.token) {
      localStorage.setItem("tbm_token", data.token);
      api.defaults.headers.common["Authorization"] = `Bearer ${data.token}`;
    }
    setUser(data);
    return data;
  };

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } catch {
      // ignore
    }
    localStorage.removeItem("tbm_token");
    delete api.defaults.headers.common["Authorization"];
    setUser(false);
  };

  return (
    <AuthContext.Provider value={{ user, checked, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
